<template>
  <ContactReviewDisplayComponent
    :accountData="accountData"
    :companyIndex="companyIndex"
    :countryCode="'EE'"
    :editableAccountFields="accountFields"
    :editableCompanyFields="companyFields"
    @updateAccountData="$emit('updateAccountData', $event)"
    @handleUpdateCompany="$emit('handleUpdateCompany', $event)"
  />
</template>

<script setup lang="ts">
import { useCompanyFinder } from "@/composables/useCompanyFinder";
import { useValidators } from "@/composables/useValidators";
import ContactReviewDisplayComponent from "@/signup/ContactReviewDisplayComponent.vue";
import type { Account, Company, ContactFieldType } from "@/types";
import { computed } from "vue";
import { useI18n } from "vue-i18n";

const props = defineProps<{
  accountData: Account;
  companyIndex?: number;
}>();

defineEmits<{
  updateAccountData: [accountData: Account];
  handleUpdateCompany: [company: Company];
}>();

const { t } = useI18n();
const {
  required,
  email,
  estonianPersonalID,
  estonianIDorCompanyCode,
  estonianYourPersonalIDifID,
  estonianVATNumber,
  personalName,
  phoneNumber,
} = useValidators(t);

const { selectedCompany } = useCompanyFinder(props.accountData);
// Fails setup with "Company not found" when the account has no matching company
void selectedCompany.value;

const accountFields: ContactFieldType[] = [
  {
    label: t("account.name"),
    id: "account-name",
    model: "name",
    type: "text",
    editable: true,
    validators: { required, personalName },
  },
  {
    label: t("account.registry_code"),
    id: "account-registry-code",
    model: "registry_code",
    type: "text",
    editable: true,
    validators: { required, estonianPersonalID },
  },
  {
    label: t("account.phone_number"),
    id: "account-phone-number",
    model: "phone_number",
    type: "tel",
    editable: true,
    validators: { required, phoneNumber },
  },
];

const companyFields: ContactFieldType[] = [
  {
    label: t("company.name"),
    id: "company-name",
    model: "name",
    type: "text",
    editable: true,
    validators: { required },
  },
  {
    label: t("company.registry_code"),
    id: "company-registry-code",
    model: "registry_code",
    type: "text",
    editable: true,
    validators: [
      required,
      estonianIDorCompanyCode,
      estonianYourPersonalIDifID(
        computed(() => props.accountData.registry_code)
      ),
    ],
  },
  {
    label: t("company.address"),
    id: "company-address",
    model: "address",
    type: "text",
    editable: true,
    validators: { required },
  },
  {
    label: t("company.vat"),
    id: "company-vat",
    model: "vat",
    type: "text",
    editable: true,
    alwaysEditable: true,
    validators: { estonianVATNumber },
  },
  {
    label: t("company.contact_email"),
    id: "company-contact-email",
    model: "contact_email",
    type: "email",
    editable: true,
    alwaysEditable: true,
    defaultValue: props.accountData.email,
    validators: { email },
  },
  {
    label: t("company.invoice_email"),
    id: "company-invoice-email",
    model: "invoice_email",
    type: "email",
    editable: true,
    alwaysEditable: true,
    defaultValue: props.accountData.email,
    validators: { email },
  },
];
</script>

<style scoped lang="scss"></style>
